<!--
 * @Description: 日程管理 - 归档管理
 * @Author: AI Assistant
 * @Date: 2023-10-30
 -->
<template>
  <div class="app-container">
    <div class="page-header">
      <!-- <el-button icon="el-icon-arrow-left" type="text" size="medium" @click="goBack">返回</el-button> -->
      <span class="page-title">归档管理</span>
    </div>
    
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>归档列表</span>
          <el-button type="primary" @click="handleAddArchive">新增归档</el-button>
        </div>
      </template>
      
      <!-- 列表 -->
      <el-table
        v-loading="loading"
        :data="archiveList"
        border
        style="width: 100%"
      >
        <el-table-column label="序号" type="index" width="60" align="center" />
        <el-table-column prop="name" label="归档名称" min-width="200" />
        <el-table-column prop="description" label="描述" min-width="300" />
        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template #default="scope">
            {{ scope.row.createdAt }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.isDeleted ? 'info' : 'success'">
              {{ scope.row.isDeleted ? '禁用' : '启用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              @click="handleViewArchive(scope.row)"
            >
              查看
            </el-button>
            <el-button
              size="small"
              type="primary"
              @click="handleEditArchive(scope.row)"
            >
              编辑
            </el-button>
            <el-button
              size="small"
              type="danger"
              @click="handleDeleteArchive(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page.sync="pageNum"
          :page-size.sync="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 归档表单弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :title="dialogType === 'add' ? '新增归档' : '编辑归档'"
      width="600px"
      append-to-body
    >
      <el-form
        ref="archiveFormRef"
        :model="archiveForm"
        :rules="rules"
        label-width="100px"
      >
        <el-form-item label="归档名称" prop="name">
          <el-input
            v-model="archiveForm.name"
            placeholder="请输入归档名称"
            maxlength="50"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="归档描述" prop="description">
          <el-input
            v-model="archiveForm.description"
            type="textarea"
            placeholder="请输入归档描述"
            :rows="4"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="archiveForm.status"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
        <el-form-item label="主题样式" prop="themeStyle">
          <el-select
            v-model="archiveForm.themeStyle"
            placeholder="请选择主题样式"
            style="width: 100%"
          >
            <el-option label="默认主题" value="default" />
            <el-option label="现代主题" value="modern" />
            <el-option label="优雅主题" value="elegant" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getArchiveList, addArchive, editArchive, deleteArchive } from '@/api/schedule/archive'
import { formatDate } from '@/utils/index'

export default {
  name: 'ArchiveManagement',
  data() {
    return {
      // 数据列表
      loading: false,
      archiveList: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,

      // 表单相关
      dialogVisible: false,
      dialogType: 'add', // 'add' 或 'edit'
      archiveForm: {
        id: '',
        name: '',
        description: '',
        status: true,
        themeStyle: 'default'
      },

      // 表单验证规则
      rules: {
        name: [
          { required: true, message: '请输入归档名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        description: [
          { max: 200, message: '最多 200 个字符', trigger: 'blur' }
        ]
      }
    }
  },

  created() {
    this.getList()
  },

  methods: {
    // 格式化日期
    formatDate,

    // 获取归档列表数据
    async getList() {
      this.loading = true
      try {
        const res = await getArchiveList({
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        
        console.log('归档数据:', res)
        
        // 处理API返回的不同数据结构
        if (res.data && Array.isArray(res.data)) {
          // 直接返回数组的情况
          this.archiveList = res.data
          this.total = res.pagination?.total || res.data.length
        } else if (res.data && res.data.records) {
          // 返回分页对象的情况
          this.archiveList = res.data.records
          this.total = res.data.total
        } else {
          this.archiveList = []
          this.total = 0
        }
      } catch (error) {
        console.error('获取归档列表失败', error)
        this.$message.error('获取归档列表失败')
      } finally {
        this.loading = false
      }
    },

    // 分页处理
    handleSizeChange(val) {
      this.pageSize = val
      this.getList()
    },

    handleCurrentChange(val) {
      this.pageNum = val
      this.getList()
    },

    // 新增归档
    handleAddArchive() {
      this.dialogType = 'add'
      this.resetForm()
      this.dialogVisible = true
    },

    // 编辑归档
    handleEditArchive(row) {
      this.dialogType = 'edit'
      this.resetForm()
      Object.assign(this.archiveForm, {
        id: row._id,
        name: row.name,
        description: row.description,
        status: !row.isDeleted,
        themeStyle: row.themeStyle || 'default'
      })
      this.dialogVisible = true
    },

    // 查看归档详情（跳转到日程矩阵视图）
    handleViewArchive(row) {
      this.$router.push({
        path: '/mall/schedule/matrix',
        query: { 
          archiveId: row._id || row.id,
          archiveName: row.name,
          view: 'matrix'
        }
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') throw err;
      });
    },

    // 删除归档
    handleDeleteArchive(row) {
      this.$confirm(
        `确定要删除归档"${row.name}"吗？此操作将永久删除该归档及其下所有日期和日程。`,
        '警告',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      )
        .then(async () => {
          try {
            await deleteArchive({ id: row._id })
            this.$message.success('删除成功')
            this.getList()
          } catch (error) {
            console.error('删除归档失败', error)
            this.$message.error('删除归档失败: ' + (error.message || '未知错误'))
          }
        })
        .catch(() => {
          this.$message.info('已取消删除')
        })
    },

    // 提交表单
    submitForm() {
      this.$refs.archiveFormRef.validate(async (valid) => {
        if (valid) {
          try {
            const formData = {
              ...this.archiveForm,
              isDeleted: !this.archiveForm.status
            }
            
            if (this.dialogType === 'add') {
              await addArchive(formData)
              this.$message.success('添加成功')
            } else {
              await editArchive(formData)
              this.$message.success('修改成功')
            }
            this.dialogVisible = false
            this.getList()
          } catch (error) {
            console.error('提交归档表单失败', error)
            this.$message.error('提交失败: ' + (error.message || '未知错误'))
          }
        }
      })
    },

    // 重置表单
    resetForm() {
      if (this.$refs.archiveFormRef) {
        this.$refs.archiveFormRef.resetFields()
      }
      Object.assign(this.archiveForm, {
        id: '',
        name: '',
        description: '',
        status: true
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.app-container {
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  margin-left: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

::v-deep .el-dialog__header {
  padding: 15px 20px;
  border-bottom: 1px solid #ebeef5;
}

::v-deep .el-dialog__body {
  padding: 20px;
}

::v-deep .el-dialog__footer {
  padding: 15px 20px;
  border-top: 1px solid #ebeef5;
}
</style>